'use client'

import Link from 'next/link'
import { memo, useCallback } from 'react'

import { buttonVariants } from '@fp/ui-kit'
import { cn } from '@lib/cn'
import { isOldRedirectEnabled, redirectsList } from '@lib/constants'

const RedirectArrow = (
  <svg width='15' height='15' viewBox='0 0 15 15' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M3.64645 11.3536C3.45118 11.1583 3.45118 10.8417 3.64645 10.6465L10.2929 4L6 4C5.72386 4 5.5 3.77614 5.5 3.5C5.5 3.22386 5.72386 3 6 3L11.5 3C11.6326 3 11.7598 3.05268 11.8536 3.14645C11.9473 3.24022 12 3.36739 12 3.5L12 9.00001C12 9.27615 11.7761 9.50001 11.5 9.50001C11.2239 9.50001 11 9.27615 11 9.00001V4.70711L4.35355 11.3536C4.15829 11.5488 3.84171 11.5488 3.64645 11.3536Z'
      fill='currentColor'
      fillRule='evenodd'
      clipRule='evenodd'
    />
  </svg>
)

export const ExternalIcon = memo(() => <span>{RedirectArrow}</span>)
ExternalIcon.displayName = 'ExternalIcon'

export const isRedirectHref = (href: string) => isOldRedirectEnabled && redirectsList.includes(href)

/** Detail routes like `/users/123` belong to the `/users` nav entry, which exact matching misses. */
export const isSectionActive = (pathname: string, href: string) => pathname === href || pathname.startsWith(`${href}/`)

type ExternalLinkProps = {
  href: string
  title: string
  pathname: string
  prefetch?: boolean
}

export const ExternalLink: React.FC<ExternalLinkProps> = memo(({ href, title, pathname, prefetch = false }) => {
  const isRedirect = isRedirectHref(href)
  // Two different questions: a detail route styles its parent as active, but only the exact page
  // is a no-op to click. Sharing one flag would block navigating back up to the list.
  const isActive = !isRedirect && isSectionActive(pathname, href)
  const isCurrentPage = !isRedirect && pathname === href

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (isCurrentPage) e.preventDefault()
    },
    [isCurrentPage],
  )

  return (
    <Link
      href={href}
      onClick={handleClick}
      className={cn(
        '!px-1 !font-normal !gap-0 !min-h-16 text-center',
        isActive
          ? '!text-primary! hover:!text-secondary-foreground'
          : '!text-foreground hover:!text-secondary-foreground',
        buttonVariants({ variant: 'link' }),
      )}
      target={isRedirect ? '_blank' : undefined}
      prefetch={prefetch}
    >
      {title}
      {isRedirect && <ExternalIcon />}
    </Link>
  )
})
ExternalLink.displayName = 'ExternalLink'
